import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PRODUCT } from "@word-duel/shared/config";
import { api, ApiError } from "../lib/api";
import { formatDateTime, TIMER_CHOICES } from "../lib/format";
import { isGameCode, listStoredGames, normalizeGameCode, saveStoredGame } from "../lib/storage";
import { Alert, AppShell, Button, Field } from "../components/ui";

export function LandingScreen() {
  const navigate = useNavigate();
  const [nickname, setNickname] = useState("");
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timerIndex, setTimerIndex] = useState(5);
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const games = useMemo(() => listStoredGames(), []);

  const createGame = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = nickname.trim();
    if (!trimmed) {
      setError("Enter a nickname to create a game.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const choice = TIMER_CHOICES[timerIndex];
      const result = await api.createGame(trimmed, timerEnabled && choice ? choice.seconds : null);
      saveStoredGame({
        code: result.game.code,
        token: result.token,
        seat: result.seat,
        nickname: result.game.me.nickname,
        createdAt: result.game.createdAt
      });
      navigate(`/game/${result.game.code}`);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Could not create the game. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const openJoin = (event: FormEvent) => {
    event.preventDefault();
    const code = normalizeGameCode(joinCode);
    if (!isGameCode(code)) {
      setError("Game codes contain 10 letters or numbers.");
      return;
    }
    navigate(`/join/${code}`);
  };

  return (
    <AppShell>
      <main className="landing">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero__copy">
            <p className="eyebrow">PRIVATE / ASYNC / TWO PLAYERS</p>
            <h1 id="hero-title">
              WORDS IN.
              <br />
              <mark>GLOVES OFF.</mark>
            </h1>
            <p className="hero__lead">
              Build the board one turn at a time. Share one private code. Come back whenever the
              next move is yours.
            </p>
            <ol className="how-it-works" aria-label="How it works">
              <li>
                <span>01</span> Name yourself
              </li>
              <li>
                <span>02</span> Invite a rival
              </li>
              <li>
                <span>03</span> Own the board
              </li>
            </ol>
          </div>

          <form className="create-panel" onSubmit={createGame} data-testid="create-form">
            <p className="panel-number">01 / START</p>
            <h2>Create a private game</h2>
            <Field
              label="Your nickname"
              hint="Visible only inside this game. Maximum 24 characters."
            >
              <input
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
                maxLength={24}
                autoComplete="nickname"
                placeholder="e.g. Tile Tyrant"
                data-testid="create-nickname"
              />
            </Field>

            <div className="timer-control">
              <div className="timer-control__heading">
                <div>
                  <span className="field__label">Turn timer</span>
                  <span className="field__hint">The server enforces each deadline.</span>
                </div>
                <button
                  className={`toggle ${timerEnabled ? "is-on" : ""}`}
                  type="button"
                  role="switch"
                  aria-checked={timerEnabled}
                  onClick={() => setTimerEnabled((value) => !value)}
                  data-testid="timer-toggle"
                >
                  <span aria-hidden="true" /> {timerEnabled ? "ON" : "OFF"}
                </button>
              </div>
              {timerEnabled && (
                <label className="range-field">
                  <input
                    type="range"
                    min="0"
                    max={TIMER_CHOICES.length - 1}
                    step="1"
                    value={timerIndex}
                    onChange={(event) => setTimerIndex(Number(event.target.value))}
                    aria-label="Time allowed per turn"
                    data-testid="timer-slider"
                  />
                  <strong>{TIMER_CHOICES[timerIndex]?.label}</strong>
                </label>
              )}
            </div>

            {error && <Alert tone="error">{error}</Alert>}
            <Button
              variant="primary"
              className="button--huge"
              type="submit"
              disabled={busy}
              data-testid="create-game"
            >
              {busy ? "Building game…" : "Create game →"}
            </Button>
          </form>
        </section>

        <section className="join-strip" aria-labelledby="join-title">
          <div>
            <p className="panel-number">02 / JOIN</p>
            <h2 id="join-title">Already invited?</h2>
          </div>
          <form onSubmit={openJoin} className="code-form">
            <label htmlFor="join-code">Enter the 10-character code</label>
            <div>
              <input
                id="join-code"
                value={joinCode}
                onChange={(event) => setJoinCode(normalizeGameCode(event.target.value))}
                maxLength={10}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                placeholder="AB12CD34EF"
                data-testid="join-code"
              />
              <Button variant="primary" type="submit" data-testid="join-go">
                Join →
              </Button>
            </div>
          </form>
        </section>

        <section className="my-games" aria-labelledby="my-games-title" data-testid="my-games">
          <div className="section-heading">
            <div>
              <p className="eyebrow">THIS DEVICE / PRIVATE INDEX</p>
              <h2 id="my-games-title">My games</h2>
            </div>
            <span className="count-block">{games.length.toString().padStart(2, "0")}</span>
          </div>
          {games.length > 0 ? (
            <div className="game-list">
              {games.map((game) => (
                <Link className="game-list__item" to={`/game/${game.code}`} key={game.code}>
                  <span>
                    <strong>{game.code}</strong>
                    <small>{game.nickname || "Saved player"}</small>
                  </span>
                  <span>
                    <small>Last opened</small>
                    <time dateTime={game.lastOpenedAt}>{formatDateTime(game.lastOpenedAt)}</time>
                  </span>
                  <b aria-hidden="true">→</b>
                </Link>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <span aria-hidden="true">□</span>
              <div>
                <h3>No saved games yet</h3>
                <p>Create one above or open an invite link.</p>
              </div>
            </div>
          )}
          <p className="storage-note">
            <strong>Keep your private resume link.</strong> {PRODUCT.name} has no accounts. Clearing
            this browser’s site data removes this list and its saved access keys.
          </p>
        </section>
      </main>
    </AppShell>
  );
}
